import { useState } from "react";
import { useApp } from "@/App";
import { motion } from "framer-motion";
import { Sparkles, Shield, Paintbrush, Car, Droplets, Eye, Wrench, Gem } from "lucide-react";

const iconMap = {
  udrzba: Car,
  voskovani: Shield,
  menu: Sparkles,
  detailing: Gem,
};

export default function ServicesSection() {
  const { packages } = useApp();

  const categories = [
    { key: "udrzba", label: "Údržbové programy" },
  ];

  const [activeCategory, setActiveCategory] = useState("udrzba");
  const filteredPackages = packages.filter(p => p.category === activeCategory);

  return (
    <section id="services" className="py-24 md:py-32 bg-[#050505]" data-testid="services-section" aria-label="Služby Car Spa">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#39FF14] mb-4">
            Naše služby
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase">
            Co pro vás <span className="text-[#39FF14]">můžeme udělat</span>
          </h2>
        </motion.div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                activeCategory === cat.key
                  ? "bg-[#39FF14] text-black"
                  : "border border-white/10 text-zinc-400 hover:border-white/20 hover:text-white"
              }`}
              data-testid={`category-${cat.key}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPackages.map((pkg, index) => {
            const Icon = iconMap[pkg.category] || Eye;
            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="service-line relative overflow-hidden bg-[#0A0A0A] border border-white/5 p-8 group hover:border-[#39FF14]/30 transition-border-color duration-500 cursor-pointer"
                onClick={() => {
                  const el = document.querySelector("#booking");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid={`service-card-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="badge-recommended px-3 py-1">
                      Nejoblíbenější
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#39FF14]/10 border border-[#39FF14]/20">
                    <Icon className="w-5 h-5 text-[#39FF14]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold uppercase tracking-wide text-white">
                      {pkg.name}
                    </h3>
                    <p className="text-sm text-zinc-500 mt-1">{pkg.duration}</p>
                  </div>
                </div>

                <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                  {pkg.description}
                </p>

                <ul className="space-y-2 mb-8">
                  {pkg.features.slice(0, 4).map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                      <div className="w-1 h-1 bg-[#39FF14]" />
                      {f}
                    </li>
                  ))}
                  {pkg.features.length > 4 && (
                    <li className="text-xs text-zinc-600">
                      + {pkg.features.length - 4} dalších
                    </li>
                  )}
                </ul>

                <div className="flex items-end justify-between">
                  <div>
                    {pkg.price > 0 ? (
                      <>
                        <span className="text-2xl font-black text-white">{pkg.price.toLocaleString()}</span>
                        <span className="text-sm text-zinc-500 ml-1">Kč</span>
                      </>
                    ) : (
                      <span className="text-lg font-bold text-[#39FF14]">Na konzultaci</span>
                    )}
                  </div>
                  <span className="text-xs text-[#39FF14] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Rezervovat →
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Surcharge note */}
        <div className="mt-8 text-xs text-zinc-600 flex flex-wrap gap-6">
          <span>SUV, crossovery = XL <span className="text-zinc-400">+20%</span></span>
          <span>Dodávky, multivany = XXL <span className="text-zinc-400">+30%</span></span>
        </div>
      </div>
    </section>
  );
}
